import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApplicationIcon,
  Box,
  Button,
  ButtonIcon,
  Icon,
  Menu,
  Popover,
  Typography,
} from '@saviynt/design-system';
import { select } from 'd3-selection';

import { butterflyData, type NodeData } from './Butterfly2Data';

import './ButterflyGraph2.css';

interface NodePosition {
  x: number;
  y: number;
  data: NodeData;
  level: number;
}

type ClusterType = 'application' | 'identity' | 'vault';

const Node = memo(
  ({
    x,
    y,
    nodeData,
    level,
    activePopoverId,
    setActivePopoverId,
    zoomedNodeId,
    setZoomedNodeId,
    openSidePanel,
  }: {
    x: number;
    y: number;
    nodeData: NodeData;
    level: number;
    activePopoverId: string | null;
    setActivePopoverId: (id: string | null) => void;
    zoomedNodeId: string | null;
    setZoomedNodeId: (id: string | null) => void;
    openSidePanel: (node: NodeData) => void;
  }) => {
    const isRoot = level === 0;
    const size = isRoot ? 66 : 50;
    const iconSize = isRoot ? 'xxLarge' : 'small';

    const isPopoverOpen =
      activePopoverId === nodeData.id && zoomedNodeId !== nodeData.id;

    const cardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          zoomedNodeId === nodeData.id &&
          cardRef.current &&
          !cardRef.current.contains(event.target as Node)
        ) {
          setZoomedNodeId(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [zoomedNodeId, nodeData.id, setZoomedNodeId]);

    return (
      <>
        {zoomedNodeId !== nodeData.id && (
          <div
            className='AOB-graphs-butterflynode-wrapper'
            style={{
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              backgroundColor: '#f2f4f8',
              borderRadius: '50%',
            }}>
            <Popover
              isPortalEnabled
              isOpen={
                !!nodeData.appName &&
                isPopoverOpen &&
                !isRoot &&
                !nodeData.onhover
              }
              setIsOpen={(open) => {
                if (nodeData.appName) {
                  setActivePopoverId(open ? nodeData.id : null);
                }
              }}
              trigger={
                <div
                  className='AOB-graphs-butterflynode-trigger'
                  role='button'
                  tabIndex={0}
                  style={{ width: size, height: size }}
                  onClick={() => {
                    if (nodeData.onhover) {
                      setZoomedNodeId(
                        zoomedNodeId === nodeData.id ? null : nodeData.id
                      );
                    } else if (nodeData.appName) {
                      setActivePopoverId(isPopoverOpen ? null : nodeData.id);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      (e.key === 'Enter' || e.key === ' ') &&
                      nodeData.appName
                    ) {
                      e.preventDefault();
                      setActivePopoverId(isPopoverOpen ? null : nodeData.id);
                    }
                  }}>
                  {nodeData.appName ? (
                    <ApplicationIcon logo={nodeData.appName} size={iconSize} />
                  ) : (
                    <ButtonIcon
                      kind='bold'
                      size='large'
                      icon={<Icon kind='Add' size='xSmall' />}
                    />
                  )}
                </div>
              }
              popoverContent={
                <Box className='AOB-graphs-popover-content'>
                  <Typography kind='h4'>{nodeData.appName}</Typography>
                  <Typography kind='body2'>ID: {nodeData.id}</Typography>
                  <Typography kind='body2'>
                    Category: {nodeData.category}
                  </Typography>
                  <Button
                    type='button'
                    kind='filled'
                    size='small'
                    rightIcon={<Icon kind='ArrowRight' size='smallMedium' />}>
                    Setup connection
                  </Button>
                </Box>
              }
            />
          </div>
        )}
        {nodeData.onhover && zoomedNodeId === nodeData.id && (
          <div ref={cardRef}>
            <Box
              className='AOB-graphs-butterflynode-card'
              style={{
                position: 'absolute',
                left: x - 50,
                top: y - 25,
                width: 'auto',
                height: 50,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                borderRadius: '12px',
                padding: '0.5rem',
                backgroundColor: '#FFFFFF',
                border: '1px solid #6888B5',
                boxShadow: '0 0 4px rgba(104, 136, 181, 0.4)',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}>
                {nodeData.appName ? (
                  <ApplicationIcon logo={nodeData.appName} size='small' />
                ) : (
                  <Icon kind='Add' size='small' />
                )}
                <Typography kind='body3'>
                  {nodeData.appName ?? 'Unnamed App'}
                </Typography>
              </div>
              <Menu
                options={[
                  { label: 'Connect', value: 'connect' },
                  { label: 'Remove', value: 'remove' },
                  { label: 'View Details', value: 'details' },
                ]}
                onChange={(val) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  if (val?.value === 'details') {
                    openSidePanel(nodeData);
                  }
                }}
                trigger={
                  <ButtonIcon
                    kind='ghost'
                    size='medium'
                    icon={<Icon kind='MoreVertical' size='medium' />}
                  />
                }
              />
            </Box>
          </div>
        )}

        {/* Labels for root */}
        {isRoot && <RootLabels x={x} y={y} />}

        {/* Label below nodes */}
        {!isRoot && zoomedNodeId !== nodeData.id && (
          <div
            className='AOB-graphs-butterflynode-label'
            style={{ left: x + 50, top: y - 10 }}>
            <Typography kind='body3' color='--color-background-neutral-bold'>
              {nodeData.appName}
            </Typography>
          </div>
        )}
      </>
    );
  }
);

const RootLabels = memo(({ x, y }: { x: number; y: number }) => {
  const labels = [
    {
      text: 'Get Started',
      kind: 'display4-bold',
      textLeft: x,
      textTop: y - 400,
    },

    {
      text: 'Applications (71)',
      kind: 'h2',
      textLeft: x + 600,
      textTop: y,
      iconName: 'applications',
      iconLeft: x + 585,
      iconTop: y - 30,
    },
    {
      iconName: 'AddCircle',
      iconLeft: x + 585,
      iconTop: y + 30,
      iconSize: 'large',
    },
    {
      iconName: 'MoreVertical',
      iconLeft: x + 680,
      iconTop: y,
      iconSize: 'medium',
    },
    {
      textLeft: x - 550,
      textTop: y - 200,
      text: 'Identity Sources',
      kind: 'h2',
      iconName: 'identities',
      iconLeft: x - 570,
      iconTop: y - 230,
    },
    {
      iconName: 'AddCircle',
      iconLeft: x - 570,
      iconTop: y - 170,
      iconSize: 'large',
    },
    {
      text: 'Vaults',
      kind: 'h2',
      textLeft: x - 550,
      textTop: y + 200,
      iconName: 'SSO',
      iconLeft: x - 560,
      iconTop: y + 160,
      iconSize: 'large',
    },
    {
      iconName: 'AddCircle',
      iconLeft: x - 560,
      iconTop: y + 230,
      iconSize: 'large',
    },
  ];

  return (
    <>
      {labels.map((label, index) => (
        <React.Fragment key={`label-${label.iconName ?? index}`}>
          {/* Render icon only if defined */}
          {label.iconName && (
            <div
              style={{
                position: 'absolute',
                left: label.iconLeft,
                top: label.iconTop,
                pointerEvents: 'none',
              }}>
              <Icon kind={label.iconName} size={label.iconSize} />
            </div>
          )}

          {/* Render text only if defined */}
          {label.text && (
            <div
              style={{
                position: 'absolute',
                left: label.textLeft,
                top: label.textTop,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                pointerEvents: 'none',
              }}>
              <Typography kind={label.kind}>{label.text}</Typography>
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );
});

function reorderCenterOut<T>(arr: T[]): T[] {
  const center = Math.floor(arr.length / 2);
  const result: T[] = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < arr.length; i++) {
    const offset = Math.floor((i + 1) / 2);
    const index = i % 2 === 0 ? center + offset : center - offset;

    if (arr[index]) {
      result.push(arr[index]);
    }
  }

  return result;
}

/**
 * Butterfly Graph
 */
function ButterflyGraph2({
  openSidePanel,
}: {
  openSidePanel: (node: NodeData) => void;
}) {
  const width = 1500;
  const height = 1000;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [nodePositions, setNodePositions] = useState<NodePosition[]>([]);
  const [redLinkIcons, setRedLinkIcons] = useState<{ x: number; y: number }[]>(
    []
  );

  const [zoomedNodeId, setZoomedNodeId] = useState<string | null>(null);

  // Memoize center coordinates
  const center = useMemo(
    () => ({ x: width / 2, y: height / 2 }),
    [width, height]
  );

  let applications = butterflyData.filter((d) => d.category === 'applications');

  const hasNoApplications = applications.length === 0;

  if (hasNoApplications) {
    applications = [
      {
        id: 'missing-app',
        appName: '',
        category: 'applications',
        status: 'connected',
      },
    ];
  }

  let identities = butterflyData.filter((d) => d.category === 'identity');
  let vaults = butterflyData.filter((d) => d.category === 'vaults');

  if (identities.length === 0) {
    identities = [
      {
        id: 'missing-identity',
        appName: '',
        category: 'identity',
        status: 'connected',
      },
    ];
  }

  if (vaults.length === 0) {
    vaults = [
      {
        id: 'missing-vault',
        appName: '',
        category: 'vaults',
        status: 'connected',
      },
    ];
  }

  const [scrollAngle] = useState(0);

  const ANGLE_STEP = 0.125;
  const VISIBLE_NODE_COUNT = 11;
  const needsScroll = applications.length > VISIBLE_NODE_COUNT;

  const [scrollIndex, setScrollIndex] = useState(0);

  const visibleApps = needsScroll
    ? applications.slice(scrollIndex, scrollIndex + VISIBLE_NODE_COUNT)
    : reorderCenterOut(applications);

  const generateAngleOffsets = (count: number) => {
    const centerOffset = Math.floor(count / 2);

    return Array.from(
      { length: count },
      (_, i) => (i - centerOffset) * ANGLE_STEP
    );
  };

  const angleOffsets = useMemo(
    () => generateAngleOffsets(visibleApps.length),
    [visibleApps]
  );

  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!needsScroll) {
      return;
    }

    const el = scrollWrapperRef.current;

    const handleScroll = (event: WheelEvent) => {
      setScrollIndex((i) =>
        event.deltaY > 0
          ? Math.min(i + 1, applications.length - VISIBLE_NODE_COUNT)
          : Math.max(i - 1, 0)
      );
    };

    el?.addEventListener('wheel', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => el?.removeEventListener('wheel', handleScroll);
  }, [applications.length, needsScroll]);

  useEffect(() => {
    const svg = select(svgRef.current);

    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);
    const g = svg.append('g');

    const nodeList: NodePosition[] = [];
    const root: NodeData = { id: 'Saviynt', appName: 'Saviynt' };

    nodeList.push({ x: center.x, y: center.y, data: root, level: 0 });

    const iconMidpoints: { x: number; y: number }[] = [];

    const drawCurvedPath = (
      x: number,
      y: number,
      node: NodeData,
      type: string
    ) => {
      const dx = x - center.x;
      const dy = y - center.y;
      const radius = Math.sqrt(dx * dx + dy * dy);

      let sweepFlag: number | null;

      if (y > center.y) {
        sweepFlag = 1;
      } else if (y < center.y) {
        sweepFlag = 0;
      } else {
        sweepFlag = null;
      }

      if (type === 'application' && sweepFlag !== null) {
        sweepFlag = 1 - sweepFlag;
      }

      const path =
        sweepFlag === null
          ? `M ${center.x},${center.y} L ${x},${y}`
          : `M ${center.x},${center.y} A ${radius} ${radius} 0 0 ${sweepFlag} ${x},${y}`;

      const isNotConnected = node.status === 'notconnected';

      const pathElement = g
        .append('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', isNotConnected ? '#CC1C10' : '#D6DFED')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', node.status === 'partial' ? '4,6' : '0');

      if (isNotConnected) {
        const nativePath = pathElement.node();

        if (nativePath) {
          const midpoint = nativePath.getPointAtLength(
            nativePath.getTotalLength() / 2
          );

          iconMidpoints.push({ x: midpoint.x, y: midpoint.y });
        }
      }
    };

    const drawStaticApplicationArc = () => {
      const staticRadius = 600;
      const startAngle = 0.125 * 6;
      const endAngle = -0.125 * 6;

      const x1 = center.x - staticRadius * Math.cos(startAngle) + 1000;
      const y1 = center.y + staticRadius * Math.sin(startAngle);

      const x2 = center.x - staticRadius * Math.cos(endAngle) + 1000;
      const y2 = center.y + staticRadius * Math.sin(endAngle);

      const path = `
    M ${x1},${y1}
    A ${staticRadius} ${staticRadius} 0 0 1 ${x2},${y2}
  `;

      g.append('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', '#979797')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '0');
    };

    const drawStaticLeftArcs = () => {
      const staticRadius = 600;
      const identityStartAngle = -0.6;
      const identityEndAngle = -0.1;

      const vaultStartAngle = 0.1;
      const vaultEndAngle = 0.5;

      // Identity Arc
      const x1Id =
        center.x + staticRadius * Math.cos(identityStartAngle) - 1000;
      const y1Id = center.y + staticRadius * Math.sin(identityStartAngle);
      const x2Id = center.x + staticRadius * Math.cos(identityEndAngle) - 1000;
      const y2Id = center.y + staticRadius * Math.sin(identityEndAngle);

      g.append('path')
        .attr(
          'd',
          `M ${x1Id},${y1Id} A ${staticRadius} ${staticRadius} 0 0 1 ${x2Id},${y2Id}`
        )
        .attr('fill', 'none')
        .attr('stroke', '#979797')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '0');

      // Vault Arc
      const x1Vault =
        center.x + staticRadius * Math.cos(vaultStartAngle) - 1000;
      const y1Vault = center.y + staticRadius * Math.sin(vaultStartAngle);
      const x2Vault = center.x + staticRadius * Math.cos(vaultEndAngle) - 1000;
      const y2Vault = center.y + staticRadius * Math.sin(vaultEndAngle);

      g.append('path')
        .attr(
          'd',
          `M ${x1Vault},${y1Vault} A ${staticRadius} ${staticRadius} 0 0 1 ${x2Vault},${y2Vault}`
        )
        .attr('fill', 'none')
        .attr('stroke', '#979797')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '0');
    };

    const drawLinearCluster = (items: NodeData[], type: ClusterType) => {
      const nodeRadius = 600;
      const tempNodes: NodePosition[] = [];

      if (type === 'application') {
        items.forEach((node, i) => {
          const angle = angleOffsets[i];
          const x = center.x - nodeRadius * Math.cos(angle) + 1000;
          const y = center.y + nodeRadius * Math.sin(angle);

          const nodePosition = { x, y, data: node, level: 1 };

          nodeList.push(nodePosition);
          tempNodes.push(nodePosition);
          drawCurvedPath(x, y, node, 'application');
        });
      }

      if (type === 'identity' || type === 'vault') {
        const offsets = generateAngleOffsets(items.length);

        items.forEach((node, i) => {
          const angle = offsets[i] + (type === 'identity' ? -0.3 : 0.3);

          const x = center.x + nodeRadius * Math.cos(angle) - 1000;
          const y = center.y + nodeRadius * Math.sin(angle);

          const nodePosition = { x, y, data: node, level: 1 };

          nodeList.push(nodePosition);
          tempNodes.push(nodePosition);
          drawCurvedPath(x, y, node, type);
        });
      }
    };

    drawLinearCluster(visibleApps, 'application');
    drawStaticApplicationArc();
    drawLinearCluster(identities, 'identity');
    drawLinearCluster(vaults, 'vault');
    drawStaticLeftArcs();

    setNodePositions(nodeList);
    setRedLinkIcons(iconMidpoints);
  }, [
    angleOffsets,
    applications,
    center,
    identities,
    scrollAngle,
    vaults,
    visibleApps,
  ]);

  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);

  return (
    <div className='AOB-graphs-container'>
      <svg
        ref={svgRef}
        className='graph-svg'
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
      <div
        className='application-wing-scroll-wrapper'
        ref={scrollWrapperRef}
        style={{ overflow: needsScroll ? 'auto' : 'hidden' }}>
        {nodePositions
          .filter(
            (node) =>
              node.data.category === 'applications' ||
              node.data.id === 'Saviynt'
          )
          .map(({ x, y, data, level }) => (
            <Node
              key={data.id}
              x={x}
              y={y}
              nodeData={data}
              level={level}
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
              zoomedNodeId={zoomedNodeId}
              setZoomedNodeId={setZoomedNodeId}
              openSidePanel={openSidePanel}
            />
          ))}
      </div>

      {nodePositions
        .filter((node) => node.data.category !== 'applications')
        .map(({ x, y, data, level }) => (
          <Node
            key={data.id}
            x={x}
            y={y}
            nodeData={data}
            level={level}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            zoomedNodeId={null}
            setZoomedNodeId={() => {}}
            openSidePanel={() => {}}
          />
        ))}

      {redLinkIcons.map((icon) => (
        <div
          key='red-icon-'
          className='butterfly-alert-icon'
          style={{
            position: 'absolute',
            left: icon.x - 12,
            top: icon.y - 12,
            width: 24,
            height: 24,
            zIndex: 2,
          }}>
          <Icon kind='AlertCriticalOutline' color='critical-700' size='small' />
        </div>
      ))}
    </div>
  );
}

export default ButterflyGraph2;
